import { DELIVERABLE_REGISTRY, isMain } from '@discovr/types';
import type { DeliverableInstance, SnapshotMetrics } from '@discovr/types';

/**
 * Optimistic budget / reach / CPV, mirroring the Go backend so the Creators
 * tab can update Total Payable + Est CPV instantly on deliverable selection.
 * MIRROR SOURCE: discovr/internal/roster/kpi/formula.go + service.go. These
 * constants are duplicated on purpose; the value is optimistic and the roster
 * refetch (onSettled) is authoritative, so any drift self-heals on next fetch.
 */

/** Reach multiplier per MAIN deliverable type (formula.go). */
const REACH_MULTIPLIER: Record<string, number> = {
  youtube_integrated: 0.7,
  youtube_dedicated: 1.0,
  youtube_shorts: 1.4,
  instagram_stories: 0.4,
  instagram_static_post: 0.6,
  instagram_carousel: 0.8,
  instagram_non_collab_reel: 1.2,
  instagram_collab_reel: 1.5,
};

/** Engagement-rate baseline (percent) per platform (formula.go). */
const ENGAGEMENT_BASELINE: Record<string, number> = {
  youtube: 3.0,
  instagram: 1.5,
};

/** Coerce an untrusted cache value to a finite number. */
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export interface RosterEstimates {
  budget: number;
  reach: number;
  cpv: number;
}

/**
 * Estimated views for a single MAIN deliverable:
 *   avg_views × multiplier × (1 + max(0, (engagement_rate − baseline) / 100)).
 * Returns 0 for unknown/non-main types (mirrors the BE warn-then-0 path).
 */
export function reachForDeliverable(type: string, snapshot: SnapshotMetrics): number {
  const multiplier = REACH_MULTIPLIER[type];
  if (multiplier === undefined) return 0;
  const platform = DELIVERABLE_REGISTRY[type]?.platform ?? '';
  const baseline = ENGAGEMENT_BASELINE[platform] ?? 0;
  const avgViews = Math.max(0, num(snapshot.avg_views));
  const engagementRate = num(snapshot.engagement_rate);
  const boost = 1 + Math.max(0, (engagementRate - baseline) / 100);
  return avgViews * multiplier * boost;
}

/** The one main whose reach and CPV the UI shows, mirroring the backend: the flagged
 *  primary, or the only selected main. Several selected mains with no single flag is
 *  ambiguous and yields null. */
function primaryOf(deliverables: DeliverableInstance[]): DeliverableInstance | null {
  const selectedMains = deliverables.filter((d) => {
    const platform = DELIVERABLE_REGISTRY[d.type]?.platform ?? '';
    return d.is_selected && isMain(d.type, platform);
  });
  if (selectedMains.length === 1) return selectedMains[0] ?? null;
  const flagged = selectedMains.filter((d) => d.is_primary);
  return flagged.length === 1 ? (flagged[0] ?? null) : null;
}

/**
 * Budget / reach / CPV for a roster entry from its current deliverable selection.
 *  budget = Σ base_price of ALL selected (main + add-ons + secondary mains)
 *  reach  = reach of the PRIMARY main only (the screens show the primary's numbers)
 *  cpv    = primary cost ÷ primary reach, or 0 when reach ≤ 0
 */
export function computeRosterEstimates(
  deliverables: DeliverableInstance[],
  snapshot: SnapshotMetrics,
): RosterEstimates {
  let budget = 0;
  for (const d of deliverables) {
    if (d.is_selected) budget += Math.max(0, num(d.base_price));
  }

  const primary = primaryOf(deliverables);
  const reach = primary ? reachForDeliverable(primary.type, snapshot) : 0;
  const mainCost = primary ? Math.max(0, num(primary.base_price)) : 0;
  const cpv = reach > 0 ? mainCost / reach : 0;
  return { budget, reach, cpv: Number.isFinite(cpv) ? cpv : 0 };
}
